import type { MaintenanceRecord } from '../types';

type RecordTypeFields = Pick<
  MaintenanceRecord,
  'title' | 'interval_hours' | 'reason' | 'schedule_id'
>;

/** Нормализованный interval_hours (API иногда отдаёт строку). */
const recordIntervalHours = (
  record: Pick<MaintenanceRecord, 'interval_hours'>
): number | null => {
  if (record.interval_hours == null) return null;
  const hours = Number(record.interval_hours);
  return Number.isFinite(hours) ? hours : null;
};

const titleLooks50 = (title: string) =>
  title === 'ТО-50' || /(?:^|[^0-9])50(?:$|[^0-9])/.test(title) || /to[-\s]?50/i.test(title);

const titleLooks100 = (title: string) =>
  title === 'ТО-100' ||
  /(?:^|[^0-9])100(?:$|[^0-9])/.test(title) ||
  /to[-\s]?100/i.test(title);

/** ТО-100 в API всегда с reason; ТО-50 — без reason. */
const reasonLooks100 = (record: Pick<MaintenanceRecord, 'reason'>) =>
  record.reason === 'initial' ||
  record.reason === 'hours' ||
  record.reason === 'year';

export const isTo50Record = (record: RecordTypeFields): boolean => {
  const hours = recordIntervalHours(record);
  if (hours != null && Math.abs(hours - 50) < 0.001) return true;
  if (hours != null && Math.abs(hours - 100) < 0.001) return false;
  if (reasonLooks100(record)) return false;
  const title = (record.title || '').trim();
  if (titleLooks100(title)) return false;
  if (titleLooks50(title)) return true;
  // Нет interval/title: запись без reason — с высокой вероятностью ТО-50
  if (!title || title === 'ТО') return !reasonLooks100(record);
  return false;
};

export const maintenanceReasonLabel = (
  reason?: 'initial' | 'hours' | 'year' | null
) => {
  if (reason === 'initial') return 'Стартовое ТО';
  if (reason === 'hours') return 'Прошло 100 м/ч';
  if (reason === 'year') return 'Прошёл год';
  return '';
};

/** Подпись типа ТО: ТО-50 / ТО-100. */
export const recordTitle = (record: RecordTypeFields): string => {
  const hours = recordIntervalHours(record);
  if (hours != null && Math.abs(hours - 50) < 0.001) return 'ТО-50';
  if (hours != null && Math.abs(hours - 100) < 0.001) return 'ТО-100';
  if (reasonLooks100(record)) return 'ТО-100';
  const title = (record.title || '').trim();
  if (titleLooks50(title)) return 'ТО-50';
  if (titleLooks100(title)) return 'ТО-100';
  if (title && title !== 'ТО') return title;
  // Старый API без interval/title: reason только у ТО-100
  return reasonLooks100(record) ? 'ТО-100' : 'ТО-50';
};

/** Порядковый номер внутри типа (ТО-50 отдельно от ТО-100). */
export const getMaintenanceNumbers = (
  records: MaintenanceRecord[]
): Record<number, number> => {
  const numbers: Record<number, number> = {};
  const byKey = new Map<string, MaintenanceRecord[]>();

  records.forEach((record) => {
    const key = isTo50Record(record) ? 'to50' : 'to100';
    const list = byKey.get(key) || [];
    list.push(record);
    byKey.set(key, list);
  });

  byKey.forEach((list) => {
    [...list]
      .sort((a, b) => {
        const dateA = String(a.date);
        const dateB = String(b.date);
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return a.id - b.id;
      })
      .forEach((record, index) => {
        numbers[record.id] = index + 1;
      });
  });

  return numbers;
};

/** Окно полётов для перепривязки: строго между соседними записями ТО. */
export const editBoundsForRecord = (
  record: Pick<MaintenanceRecord, 'id' | 'hobbs' | 'edit_min_hobbs' | 'edit_max_hobbs'>,
  records: Pick<MaintenanceRecord, 'id' | 'hobbs'>[]
): { minHobbs: number; maxHobbs?: number } => {
  const chrono = [...records].sort((a, b) =>
    a.hobbs !== b.hobbs ? a.hobbs - b.hobbs : a.id - b.id
  );
  const idx = chrono.findIndex((row) => row.id === record.id);
  const prev = idx > 0 ? chrono[idx - 1] : null;
  const next = idx >= 0 && idx < chrono.length - 1 ? chrono[idx + 1] : null;
  return {
    minHobbs: prev?.hobbs ?? record.edit_min_hobbs ?? 0,
    maxHobbs: next?.hobbs ?? record.edit_max_hobbs,
  };
};

/** Номер следующей формы каждого типа: сколько уже проведено + 1. */
export const getUpcomingMaintenanceNumbers = (
  records: MaintenanceRecord[]
): { to50: number; to100: number } => {
  let to50 = 0;
  let to100 = 0;
  records.forEach((record) => {
    if (isTo50Record(record)) to50 += 1;
    else to100 += 1;
  });
  return { to50: to50 + 1, to100: to100 + 1 };
};
